import classnames from "classnames";
import React, { Fragment } from "react";

import { getDefaultImage } from "@utils/configuration/aem";
import { ProductImageRenditions } from "@utils/enums";
import { useIsClientSide } from "@utils/hooks/isClient.hook";

import { Media } from "lib/@types/service/shared";

import "./image.scss";

type ExtendedMedia = Media & {
    media: string;
};

export type Props = {
    lazyLoad?: boolean;
    parentFit?: boolean;
    medias: Array<Media>;
    className?: string;
    altText?: string;
    defaultImageUrl?: string;
    rendition?: ProductImageRenditions;
};

const Image = ({
    lazyLoad = false,
    parentFit = false,
    medias = [],
    className,
    altText,
    defaultImageUrl,
    rendition,
}: Props) => {
    const isClient = useIsClientSide();

    const sortMedias = (medias: Array<Media>) => {
        return medias.sort((a, b) => {
            const aWidth = getWidth(a.format);
            const bWidth = getWidth(b.format);

            if (aWidth < bWidth) return 1;
            if (aWidth > bWidth) return -1;
            return 0;
        });
    };

    const getWidth = (format?: string | null) => {
        if (!format) {
            return 0;
        }
        return parseInt(format.split("x")[0], 10);
    };

    const toSrcSet = (medias: Array<ExtendedMedia>) => {
        return medias.map(m => `${m.url} ${getWidth(m.format)}w`).join(",");
    };

    const getMinWidth = (format?: string | null) => {
        if (format) {
            const width = getWidth(format);
            return `(min-width:${width}px)`;
        }
        return "(min-width:0px)";
    };

    const calculateMediaValue = (medias: Array<Media> = []) => {
        const sortedMedias = sortMedias(medias);
        return sortedMedias.map<ExtendedMedia>((m, index) => {
            const newIndex = index + 1;
            const previousMedia =
                newIndex < sortedMedias.length
                    ? sortedMedias[newIndex]
                    : undefined;
            return {
                ...m,
                media: getMinWidth(previousMedia?.format),
            };
        });
    };

    let srcSet: ExtendedMedia[] = calculateMediaValue(
        medias.filter(m => m.format !== ProductImageRenditions.ORIGINAL),
    );

    let originalSource = medias.find(
        m => m.format === ProductImageRenditions.MD,
    );

    if (rendition) {
        srcSet = [];
        originalSource = medias.find(m => m.format === rendition);
        if (parentFit && lazyLoad) {
            srcSet = calculateMediaValue(
                originalSource ? [originalSource] : [],
            );
        }
    }

    return (
        <Fragment>
            {lazyLoad && (
                <Fragment>
                    {!parentFit && (
                        <picture>
                            {srcSet.map((s, index) => (
                                <source
                                    key={index}
                                    srcSet={s.url}
                                    media={s.media}
                                    type={s.mimeType || undefined}
                                    width={146}
                                    height={109}
                                />
                            ))}
                            <img
                                src={originalSource?.url || defaultImageUrl || getDefaultImage()}
                                data-sizes="auto"
                                className={classnames(
                                    "image-component",
                                    className,
                                )}
                                alt={
                                    altText ||
                                    originalSource?.altText ||
                                    undefined
                                }
                                width={146}
                                height={109}
                                loading={lazyLoad ? "lazy" : "eager"}
                                decoding={lazyLoad ? "async" : "auto"}
                            />
                        </picture>
                    )}
                    {parentFit && (
                        <img
                            src={originalSource?.url || defaultImageUrl || getDefaultImage()}
                            data-sizes="auto"
                            data-parent-fit="width"
                            className={classnames("image-component", className)}
                            data-srcset={toSrcSet(srcSet)}
                            alt={altText}
                            loading="lazy"
                            decoding="async"
                            src-set={toSrcSet(srcSet)}
                            width={146}
                            height={109}
                        />
                    )}
                </Fragment>
            )}
            {!lazyLoad && (
                <picture>
                    {srcSet.map((s, index) => (
                        <source
                            key={index}
                            srcSet={s.url}
                            media={s.media}
                            type={s.mimeType || undefined}
                            width={146}
                            height={109}
                        />
                    ))}
                    {!originalSource && (
                        <img
                            src={defaultImageUrl || getDefaultImage()}
                            className={classnames("image-component", className)}
                            alt={altText}
                            width={146}
                            height={109}
                        />
                    )}
                    {!!originalSource && (
                        <img
                            src={originalSource.url}
                            className={classnames("image-component", className)}
                            alt={altText || originalSource.altText || undefined}
                            width={146}
                            height={109}
                        />
                    )}
                </picture>
            )}
        </Fragment>
    );
};

export { Image };
